import { ComponentType, useCallback, useContext, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { DocumentNode } from "graphql";
import { GroupBase, OptionsOrGroups } from "react-select";
import { Response } from "react-select-async-paginate";
import { genericMemo, usePrevious } from "../../../../utils";
import { Select, SelectProps } from "../../../form";
import { AppConfigContext } from "../../../generic";
import { constructFilter, constructOrder, FilterParamsType } from "../../builders";
import { FilterExpressionChildInput, FilterExpressionOperatorEnum, FilterOperatorEnum, Maybe, OrderEntryInput } from "../../innerTypes";
import { FilteringProps, WithOrderingProps } from "../../types";

export type  GraphQLSelectQueryResponse = {
  items: any[],
  totalPages?: number,
}

export type GraphQLSelectQueryRequest = {
  page?: number,
  size?: number,
  orders?: OrderEntryInput[],
  filter?: Maybe<FilterExpressionChildInput>,
}

export type GraphQLSelectProps<T> = SelectProps<T> & {
  query?: DocumentNode,
  queryPromise?: (request?: GraphQLSelectQueryRequest) => GraphQLSelectQueryResponse | Promise<GraphQLSelectQueryResponse>,
  queryAdditionalProps?: object,
  onError?: (e: any) => void,
  extractQueryData?: (data: any) => GraphQLSelectQueryResponse,
  emptyItem?: T,
  allowEmpty?: boolean
  pageSize?: number,
  globalFilters?: Maybe<FilterExpressionChildInput>,
} & FilteringProps<T> & WithOrderingProps<T>

export const GraphQLSelect = genericMemo(<T extends any>(
  {
    query,
    queryPromise,
    queryAdditionalProps,
    extractQueryData,
    emptyItem,
    allowEmpty,
    onError,
    pageSize,
    ordering,
    filterExpressionBuilder,
    filterOperator,
    filterAccessor,
    filterValueModifier,
    globalFilters,
    onMenuOpen,
    onMenuClose,
    ...props
  }: GraphQLSelectProps<T>) => {
  const [ isMenuOpen, setMenuOpen ] = useState(props.menuIsOpen);

  const appConfig = useContext(AppConfigContext);
  const client = useApolloClient();
  const orders = useMemo(() => constructOrder(ordering), [ ordering ]);
  const makeFilter = useCallback((value: string): FilterParamsType[] | undefined => {
    return (filterAccessor || filterExpressionBuilder) ? [ {
      expressionBuilder: filterExpressionBuilder ? (v) => filterExpressionBuilder(v, queryAdditionalProps) : undefined,
      path: (filterAccessor || "") as string,
      operator: filterOperator === undefined ? FilterOperatorEnum.Equal : filterOperator as FilterOperatorEnum,
      value: filterValueModifier ? filterValueModifier?.(value, appConfig) : value,
    } ] : undefined;
  }, [ filterExpressionBuilder, filterAccessor, filterOperator, filterValueModifier, appConfig, queryAdditionalProps ]);

  const [ loadOptions, key ] = useMemo(() => [ async (search: string, loadedOptions: OptionsOrGroups<T, GroupBase<T>>, { page }: any): Promise<Response<T, any, any>> => {
    const filter = globalFilters
      ? {
        expr: {
          operator: FilterExpressionOperatorEnum.And,
          children: [
            globalFilters,
            constructFilter(makeFilter(search)),
          ].filter(x => x),
        },
      }
      : constructFilter(makeFilter(search));

    const variables = {
      page: page - 1,
      size: pageSize,
      orders,
      filter,
      ...queryAdditionalProps,
    };
    let queryData: GraphQLSelectQueryResponse | undefined;

    try {
      if (queryPromise) {
        queryData = await queryPromise(variables);
      } else if (query) {
        const res = await client.query({
          query,
          variables,
        });
        queryData = extractQueryData?.(res?.data);
      }
    } catch (e) {
      onError?.(e);
    }

    const options = [];
    if (page === 1 && allowEmpty && emptyItem && !search) {
      options.push(emptyItem);
    }
    options.push(...queryData?.items || []);

    return {
      options,
      hasMore: (queryData?.totalPages || 1) > page,
      additional: {
        page: page + 1,
      },
    };
  }, Date.now() ], [ query, client, queryPromise, extractQueryData, emptyItem, onError, pageSize, orders, makeFilter, globalFilters, allowEmpty, queryAdditionalProps ]);

  const previousKey = usePrevious(key);

  return (
    <Select
      cacheUniqs={[ key ]}
      defaultOptions={isMenuOpen && previousKey !== undefined && previousKey !== key}
      loadOptions={loadOptions}
      placeholder="Find..."
      {...props}
      onMenuOpen={useCallback(() => {
        setMenuOpen(true);
        onMenuOpen?.();
      }, [ onMenuOpen ])}
      onMenuClose={useCallback(() => {
        setMenuOpen(false);
        onMenuClose?.();
      }, [ onMenuClose ])}
    />
  );
});


(GraphQLSelect as ComponentType<GraphQLSelectProps<any>>).defaultProps = {
  extractQueryData: (data) => Object.values(data || {})?.[0] as {
    items: any[],
    totalPages?: number,
  },
  onError: console.error,
  pageSize: 20,
};


export default GraphQLSelect;

